<script setup lang="ts">
  import { KEY_ESCAPE, setBodyOverflow } from '@travelata-libs/utils';
  import { onKeyStroke, useSwipe } from '@vueuse/core';
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
  import Teleport from 'vue2-teleport'; // eslint-disable-line @typescript-eslint/no-unused-vars
  import { Typo, Icon } from '@/components';

  const ANIMATION_DURATION = 300;

  interface BottomSheetProps {
    height?: string;
    title?: string;
    overlayClosable?: boolean;
    iconClosable?: boolean;
    swipeClosable?: boolean;
    animated?: boolean;
    zIndex?: number;
    contentClass?: string;
    rootClass?: string;
  }

  interface BottomSheetEmits {
    (e: 'close'): void;
  }

  const props = withDefaults(defineProps<BottomSheetProps>(), {
    height: 'auto',
    title: '',
    overlayClosable: true,
    swipeClosable: true,
    iconClosable: false,
    animated: false,
    zIndex: 5,
    contentClass: '',
    rootClass: '',
  });

  const emit = defineEmits<BottomSheetEmits>();

  const overlayElement = ref<HTMLDivElement>();
  const floatingRef = ref<HTMLDivElement>();
  const lineElement = ref<HTMLDivElement>();
  const contentRef = ref<HTMLDivElement | null>(null);

  const translateY = ref('0%');
  const overlayOpacity = ref(1);
  const isDragging = ref(false);
  const isClosing = ref(false);

  const overlayHeight = computed(() => overlayElement.value?.offsetHeight);
  const isSmooth = computed(() => props.animated && !isDragging.value);

  const overlayStyle = computed(() => ({
    opacity: overlayOpacity.value,
    zIndex: props.zIndex,
  }));

  const floatingStyle = computed(() => ({
    height: props.height,
    transform: `translateY(${translateY.value})`,
  }));

  const wait = (ms: number) =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, ms);
    });
  const waitTransition = (element: HTMLElement, property: string, signal: AbortSignal) =>
    new Promise<void>((resolve) => {
      element.addEventListener(
        'transitionend',
        (event: Event) => {
          const transitionEvent = event as TransitionEvent;

          if (transitionEvent.target === element && transitionEvent.propertyName === property) {
            resolve();
          }
        },
        { signal },
      );
    });

  const close = async () => {
    if (isClosing.value) {
      return;
    }

    if (!props.animated) {
      emit('close');
      return;
    }

    isClosing.value = true;
    isDragging.value = false; // возвращаем плавность (transition)
    translateY.value = '100%'; // transition сам доедет из текущей позиции, в т.ч. из drag
    overlayOpacity.value = 0;

    const floating = floatingRef.value;

    if (floating) {
      const controller = new AbortController();

      await Promise.race([waitTransition(floating, 'transform', controller.signal), wait(ANIMATION_DURATION + 50)]);
      controller.abort();
    }

    emit('close');
  };

  const onOverlayClick = () => {
    if (!props.overlayClosable) {
      return;
    }
    close();
  };

  const { lengthY } = useSwipe(lineElement, {
    onSwipeStart() {
      isDragging.value = true;
    },
    onSwipe() {
      if (lineElement.value && overlayHeight.value && lengthY.value < 0) {
        const length = Math.abs(lengthY.value);
        translateY.value = `${length}px`;
        overlayOpacity.value = 1.1 - (length * 2) / overlayHeight.value;
      } else {
        translateY.value = '0%';
        overlayOpacity.value = 1;
      }
    },
    onSwipeEnd() {
      isDragging.value = false;

      const shouldClose =
        lengthY.value < 0 && !!overlayHeight.value && Math.abs(lengthY.value) / overlayHeight.value >= 0.2;

      if (shouldClose) {
        close();
      } else {
        translateY.value = '0%';
        overlayOpacity.value = 1;
      }
    },
  });

  const resetScroll = () => {
    if (contentRef.value) {
      contentRef.value?.firstElementChild?.scrollIntoView({
        block: 'start',
        inline: 'nearest',
      });
    }
  };

  function onTouchMove(e: TouchEvent) {
    const target = e.target as HTMLElement;

    if (!contentRef.value?.contains(target)) {
      e.preventDefault();
    }
  }

  onKeyStroke(KEY_ESCAPE, (e) => {
    e.preventDefault();
    close();
  });

  onMounted(() => {
    setBodyOverflow('hidden');
    document.body.addEventListener('touchmove', onTouchMove, { passive: false });
  });

  onBeforeUnmount(() => {
    setBodyOverflow('visible');
    document.body.removeEventListener('touchmove', onTouchMove);
  });

  defineExpose({
    resetScroll,
    close,
  });
</script>

<template>
  <Teleport to="body">
    <div :class="[rootClass, 'tl-bottom-sheet']" data-test-id="bottom-sheet">
      <div
        ref="overlayElement"
        class="tl-bottom-sheet__overlay"
        :class="{
          'tl-bottom-sheet__overlay_in': animated && !isClosing,
          'tl-bottom-sheet__overlay_smooth': isSmooth,
        }"
        :style="overlayStyle"
        data-test-id="overlay"
        @click.self="onOverlayClick"
      >
        <div
          ref="floatingRef"
          :style="floatingStyle"
          class="tl-bottom-sheet__floating"
          :class="{
            'tl-bottom-sheet__floating_in': animated && !isClosing,
            'tl-bottom-sheet__floating_smooth': isSmooth,
          }"
          data-test-id="floating"
        >
          <Icon v-if="iconClosable" name="close" size="l" class="tl-bottom-sheet__icon" @click.native="close" />
          <div v-if="swipeClosable" ref="lineElement" class="tl-bottom-sheet__line-wrapper">
            <div class="tl-bottom-sheet__line" />
          </div>

          <header v-if="$slots.header || title" class="tl-bottom-sheet__header">
            <slot name="header">
              <Typo v-if="title" class="tl-bottom-sheet__title" variant="h-5" data-test-id="title">
                {{ title }}
              </Typo>
            </slot>
          </header>

          <div ref="contentRef" :class="[contentClass, 'tl-bottom-sheet__content']">
            <slot />
          </div>

          <footer v-if="$slots.footer" class="tl-bottom-sheet__footer">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
  @keyframes tl-bottom-sheet-slide-in {
    from {
      transform: translateY(100%);
    }

    to {
      transform: translateY(0);
    }
  }

  @keyframes tl-bottom-sheet-fade-in {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  .tl-bottom-sheet {
    &__overlay {
      align-items: center;
      background: var(--color-bg-modal);
      display: flex;
      flex-direction: column;
      height: 100%;
      inset: 0;
      justify-content: flex-end;
      position: fixed;
      width: 100%;

      // Открытие: keyframe проигрывается самим фактом появления элемента с классом.
      &_in {
        animation: tl-bottom-sheet-fade-in 0.3s ease;
      }

      // Закрытие/свайп-релиз: обычный transition из текущего состояния.
      &_smooth {
        transition: var(--transition-default);
      }
    }

    &__icon {
      cursor: pointer;
      position: absolute;
      right: var(--spacing-m);
      top: var(--spacing-m);
      z-index: 6;
    }

    &__floating {
      background: var(--color-bg-primary);
      border-radius: var(--rounding-xxl) var(--rounding-xxl) 0 0;
      bottom: 0;
      box-shadow: var(--shadow-m);
      box-sizing: border-box;
      color: var(--color-text-primary);
      display: flex;
      flex-direction: column;
      left: 0;
      max-height: calc(100% - var(--spacing-xl));
      min-height: 150px;
      padding: calc(var(--spacing-xs) + var(--spacing-l)) 0 var(--spacing-l);
      position: absolute;
      width: 100%;
      z-index: 10;

      // Открытие: keyframe на transform — тем же свойством, что и закрытие/свайп.
      &_in {
        animation: tl-bottom-sheet-slide-in 0.3s ease;
      }

      // Закрытие/свайп-релиз: transition на transform, конфликта с keyframe нет.
      &_smooth {
        transition: var(--transition-default);
      }
    }

    &__header {
      align-items: center;
      background-color: inherit;
      display: flex;
      gap: var(--spacing-xs);
      justify-content: space-between;
      padding: 0 var(--spacing-l) var(--spacing-l);
      position: relative;
      z-index: 5;
    }

    &__line-wrapper {
      align-items: center;
      content: '';
      display: flex;
      justify-content: center;
      left: 0;
      padding: var(--spacing-m) 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &__line {
      background-color: var(--color-text-secondary);
      border-radius: var(--rounding-xs);
      height: 4px;
      width: 34px;
    }

    &__title {
      width: 100%;
    }

    &__content {
      color: var(--color-text-secondary);
      flex: 1;
      overflow: auto;
      padding: 0 var(--spacing-l);

      @include typography-body(l, 400);
    }

    &__footer {
      align-items: center;
      background-color: inherit;
      display: flex;
      gap: var(--spacing-xs);
      padding: var(--spacing-xl) var(--spacing-l) 0;
      position: relative;
      z-index: 5;
    }
  }
</style>
